import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import SponsorMultiAdv from "core/components/SponsorMultiAdv";
import getBrandingStartSecond, {
  getBrandingDurationBucket,
} from "core/utils/inStreamBrandingSchedule";
import {
  trackBrandingEvent,
  BRANDING_SHORT_VIDEO,
  BRANDING_OPPORTUNITY,
  BRANDING_FILLED,
  BRANDING_NO_FILL,
} from "core/utils/adAnalytics";

/**
 * Инстрим-брендинг / баннер-уголок внутри плеера.
 *
 * Один показ за просмотр: секунда показа выбирается по длительности видео
 * (core/utils/inStreamBrandingSchedule), считаются только секунды
 * воспроизведения. Преролл идёт до монтирования экрана видео, поэтому в отсчёт
 * не попадает. Повторов нет ни в видео, ни в трансляциях.
 */
export default function SponsorMidAdv({
  videoState,
  duration,
  isLive,
  contentId,
  rightholderId,
  sponsorInStreamBannerPlace,
  sponsorCornerBannerPlace,
  fullscreenTarget,
  setInStreamBannerHeight,
  setCornerBannerHeight,
}) {
  const [playedTime, setPlayedTime] = useState(0);
  const [shown, setShown] = useState(false);

  // undefined: ещё не решили (ждём длительность), null: в этом просмотре не показываем
  const startSecondRef = useRef(undefined);
  if (startSecondRef.current === undefined) {
    startSecondRef.current = getBrandingStartSecond({ duration, isLive });
  }

  const startSecond = startSecondRef.current;
  const durationBucket = getBrandingDurationBucket({ duration, isLive });
  const reached =
    typeof startSecond === "number" && playedTime >= startSecond && !shown;

  // Аналитика инвентаря: по одному событию на просмотр
  const shortTrackedRef = useRef(false);
  const opportunityTrackedRef = useRef(false);

  useEffect(() => {
    if (!process.browser || startSecond !== null || shortTrackedRef.current) {
      return;
    }
    shortTrackedRef.current = true;
    trackBrandingEvent(BRANDING_SHORT_VIDEO, {
      contentId,
      durationBucket,
    });
  }, [startSecond]);

  useEffect(() => {
    if (!reached || opportunityTrackedRef.current) return;
    opportunityTrackedRef.current = true;
    trackBrandingEvent(BRANDING_OPPORTUNITY, {
      contentId,
      durationBucket,
    });
  }, [reached]);

  const onCompleted = (data) => {
    const campaign = data?.sponsorCampaign;
    const format = campaign?.cornerBanner
      ? "frame"
      : campaign?.inStreamBanner
      ? "strip"
      : undefined;

    trackBrandingEvent(format ? BRANDING_FILLED : BRANDING_NO_FILL, {
      contentId,
      durationBucket,
      format,
    });
  };
  const shouldCount =
    process.browser &&
    !shown &&
    typeof startSecond === "number" &&
    videoState.status === "playing";

  useEffect(() => {
    if (!shouldCount) return undefined;

    const timer = setInterval(() => {
      setPlayedTime((t) => t + 1);
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [shouldCount]);

  if (!process.browser) return null;
  if (shown) return null;
  if (videoState.isEnded) return null;
  if (typeof startSecond !== "number") return null;
  if (playedTime < startSecond) return null;

  const onFinish = () => {
    setShown(true);
  };

  return (
    <SponsorMultiAdv
      inStreamBannerPlace={sponsorInStreamBannerPlace}
      cornerBannerPlace={sponsorCornerBannerPlace}
      contentId={contentId}
      rightholderId={rightholderId}
      fullscreenTarget={fullscreenTarget}
      onFinish={onFinish}
      onCompleted={onCompleted}
      setInStreamBannerHeight={setInStreamBannerHeight}
      setCornerBannerHeight={setCornerBannerHeight}
    />
  );
}

SponsorMidAdv.propTypes = {
  videoState: PropTypes.shape({
    status: PropTypes.oneOf(["playing", "paused"]).isRequired,
    isEnded: PropTypes.bool,
  }).isRequired,
  duration: PropTypes.number,
  isLive: PropTypes.bool,
  contentId: PropTypes.string.isRequired,
  rightholderId: PropTypes.string.isRequired,
  sponsorInStreamBannerPlace: PropTypes.string,
  sponsorCornerBannerPlace: PropTypes.string,
  fullscreenTarget: PropTypes.string.isRequired,
  setInStreamBannerHeight: PropTypes.func.isRequired,
  setCornerBannerHeight: PropTypes.func.isRequired,
};
